import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Opengater section auth context.
 *
 * Single source of truth for "is the operator logged into Opengater, and as
 * what role?". Everything downstream — the login gate, nav visibility, action
 * buttons — reads from here rather than poking localStorage directly, so the
 * whole section re-renders coherently on login / logout / token expiry.
 *
 * The access token remains the authentication state, while the role is read
 * live from GlobalAuth `/users/me`. Unknown or unavailable roles get no
 * privileged access. We listen for:
 *   - OG_AUTH_EXPIRED_EVENT — the client hit an unrecoverable 401 → drop to login
 *   - `storage` — another tab logged in/out → mirror it here
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, useRef, } from 'react';
import { getOgAccessToken, getOgEmail } from './tokens';
import { OG_AUTH_EXPIRED_EVENT } from './client';
import { logoutOg } from './auth';
import { apiOg } from './client';
import { isOgStaff, roleRequirementAllows } from './role';
import { normalizeRole } from '@/auth/roles';
import { AUTHORIZATION_DENIED_EVENT, authorizationSource, } from '@/auth/authorizationEvents';
const ROLE_POLL_MS = 60_000;
const OgAuthContext = createContext(null);
export function OgAuthProvider({ children }) {
    const [token, setToken] = useState(() => getOgAccessToken());
    const [email, setEmail] = useState(() => getOgEmail());
    const [role, setRole] = useState(null);
    const [roleStatus, setRoleStatus] = useState(() => getOgAccessToken() ? 'loading' : 'idle');
    const roleRequest = useRef(0);
    // Capabilities the server has 403'd this session. Keeping this in memory
    // avoids persisting a possibly stale permission verdict across sessions.
    const [deniedCaps, setDeniedCaps] = useState(() => new Set());
    const deniedCapsRole = useRef(null);
    const sync = useCallback(() => {
        setToken(getOgAccessToken());
        setEmail(getOgEmail());
    }, []);
    const refreshRole = useCallback(async () => {
        if (!getOgAccessToken()) {
            roleRequest.current += 1;
            setRole(null);
            setRoleStatus('idle');
            return;
        }
        const current = ++roleRequest.current;
        setRoleStatus((previous) => (previous === 'ready' ? previous : 'loading'));
        try {
            const me = await apiOg('auth', '/users/me');
            if (current !== roleRequest.current)
                return;
            setRole(normalizeRole(me.role));
            setRoleStatus('ready');
        }
        catch {
            if (current !== roleRequest.current)
                return;
            setRole(null);
            setRoleStatus('error');
        }
    }, []);
    const markDenied = useCallback((cap) => {
        setDeniedCaps((prev) => (prev.has(cap) ? prev : new Set(prev).add(cap)));
    }, []);
    useEffect(() => {
        if (deniedCapsRole.current === role)
            return;
        deniedCapsRole.current = role;
        setDeniedCaps(new Set());
    }, [role]);
    const logout = useCallback(async () => {
        await logoutOg();
        setToken(null);
        setEmail(null);
        setRole(null);
        setRoleStatus('idle');
        // Fresh session on next login — clear remembered denials.
        setDeniedCaps(new Set());
    }, []);
    useEffect(() => {
        if (!token) {
            setRole(null);
            setRoleStatus('idle');
            return;
        }
        void refreshRole();
        const timer = window.setInterval(() => void refreshRole(), ROLE_POLL_MS);
        const onDenied = (event) => {
            if (authorizationSource(event) === 'opengater')
                void refreshRole();
        };
        window.addEventListener(AUTHORIZATION_DENIED_EVENT, onDenied);
        return () => {
            window.clearInterval(timer);
            window.removeEventListener(AUTHORIZATION_DENIED_EVENT, onDenied);
        };
    }, [token, refreshRole]);
    useEffect(() => {
        const onExpired = () => {
            setToken(null);
            setEmail(null);
            setRole(null);
            setRoleStatus('idle');
        };
        // Cross-tab: another tab wrote/cleared og_access_token. `key === null`
        // happens on localStorage.clear() — resync in that case too.
        const onStorage = (e) => {
            if (e.key === null || e.key === 'og_access_token' || e.key === 'og_email') {
                sync();
            }
        };
        window.addEventListener(OG_AUTH_EXPIRED_EVENT, onExpired);
        window.addEventListener('storage', onStorage);
        return () => {
            window.removeEventListener(OG_AUTH_EXPIRED_EVENT, onExpired);
            window.removeEventListener('storage', onStorage);
        };
    }, [sync]);
    const value = useMemo(() => {
        const can = (requirement) => roleRequirementAllows(role, requirement);
        return {
            token,
            email,
            role,
            roleStatus,
            isAuthed: !!token,
            isStaff: isOgStaff(role),
            can,
            allowed: (cap, requirement = 'admin') => can(requirement) && !deniedCaps.has(cap),
            isDenied: (cap) => deniedCaps.has(cap),
            markDenied,
            sync,
            logout,
        };
    }, [token, email, role, roleStatus, deniedCaps, markDenied, sync, logout]);
    return _jsx(OgAuthContext.Provider, { value: value, children: children });
}
export function useOgAuth() {
    const ctx = useContext(OgAuthContext);
    if (!ctx)
        throw new Error('useOgAuth must be used within <OgAuthProvider>');
    return ctx;
}
